import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Badge, Modal, Form, Alert, Spinner, InputGroup, ToggleButtonGroup, ToggleButton } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import adminFeeService from '../../services/adminFeeService';
import clientService from '../../services/clientService';

const AdminFeeConfigurationsPage = () => {
  const [configurations, setConfigurations] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [stats, setStats] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    feeType: 'percentage',
    feeValue: '',
    validFrom: new Date(),
    validTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 año desde hoy
    appliesTo: 'ALL',
    selectedClientIds: [],
    minInvestment: '',
    maxInvestment: '',
    isActive: true
  });

  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadConfigurations(),
        loadClients(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar los datos');
    } finally {
      setLoading(false);
    }
  };

  const loadConfigurations = async () => {
    try {
      const configs = await adminFeeService.getAllFeeConfigurations();
      setConfigurations(configs);
    } catch (error) {
      console.error('Error loading configurations:', error);
      toast.error('Error al cargar las configuraciones');
    }
  };

  const loadClients = async () => {
    try {
      const result = await clientService.getAllClients();
      if (result.success) {
        setClients(result.clients);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await adminFeeService.getFeeStats();
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Limpiar errores de validación
    if (validationErrors[field]) {
      setValidationErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = 'El nombre es requerido';
    }

    if (!formData.feeValue || parseFloat(formData.feeValue) <= 0) {
      errors.feeValue = 'El valor del fee debe ser mayor que 0';
    }

    if (formData.feeType === 'percentage' && parseFloat(formData.feeValue) > 100) {
      errors.feeValue = 'El porcentaje no puede ser mayor que 100%';
    }

    if (!formData.validFrom) {
      errors.validFrom = 'La fecha de inicio es requerida';
    }

    if (!formData.validTo) {
      errors.validTo = 'La fecha de fin es requerida';
    }

    if (formData.validFrom && formData.validTo && formData.validFrom >= formData.validTo) {
      errors.validTo = 'La fecha de fin debe ser posterior a la fecha de inicio';
    }

    if (formData.appliesTo === 'SPECIFIC_CLIENTS' && formData.selectedClientIds.length === 0) {
      errors.selectedClientIds = 'Debe seleccionar al menos un cliente';
    }

    if (formData.minInvestment && formData.maxInvestment && 
        parseFloat(formData.minInvestment) >= parseFloat(formData.maxInvestment)) {
      errors.maxInvestment = 'La inversión máxima debe ser mayor que la mínima';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor corrige los errores en el formulario');
      return;
    }

    try {
      setSaving(true);

      const submitData = {
        ...formData,
        validFrom: formData.validFrom.toISOString().split('T')[0],
        validTo: formData.validTo.toISOString().split('T')[0],
        feeValue: parseFloat(formData.feeValue),
        minInvestment: formData.minInvestment ? parseFloat(formData.minInvestment) : null,
        maxInvestment: formData.maxInvestment ? parseFloat(formData.maxInvestment) : null,
        selectedClientIds: formData.appliesTo === 'SPECIFIC_CLIENTS' ? formData.selectedClientIds : []
      };

      if (editingConfig) {
        await adminFeeService.updateFeeConfiguration(editingConfig.id, submitData);
        toast.success('Configuración actualizada exitosamente');
      } else {
        await adminFeeService.createFeeConfiguration(submitData);
        toast.success('Configuración creada exitosamente');
      }

      await loadConfigurations();
      await loadStats();
      handleCloseModal();
    } catch (error) {
      console.error('Error saving configuration:', error);
      toast.error('Error al guardar la configuración');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (config) => {
    setEditingConfig(config);
    setFormData({
      name: config.name,
      description: config.description || '',
      feeType: config.feeType,
      feeValue: config.feeValue.toString(),
      validFrom: new Date(config.validFrom),
      validTo: new Date(config.validTo),
      appliesTo: config.appliesTo,
      selectedClientIds: config.getSelectedClientIdsArray ? config.getSelectedClientIdsArray() : [],
      minInvestment: config.minInvestment ? config.minInvestment.toString() : '',
      maxInvestment: config.maxInvestment ? config.maxInvestment.toString() : '',
      isActive: config.isActive
    });
    setShowModal(true);
  };

  const handleDelete = (config) => {
    setConfigToDelete(config);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await adminFeeService.deleteFeeConfiguration(configToDelete.id);
      toast.success('Configuración eliminada exitosamente');
      await loadConfigurations();
      await loadStats();
      setShowDeleteModal(false);
      setConfigToDelete(null);
    } catch (error) {
      console.error('Error deleting configuration:', error);
      toast.error('Error al eliminar la configuración');
    }
  };

  const handleToggleActive = async (config) => {
    try {
      await adminFeeService.toggleFeeConfiguration(config.id);
      toast.success(`Configuración ${config.isActive ? 'desactivada' : 'activada'} exitosamente`);
      await loadConfigurations();
      await loadStats();
    } catch (error) {
      console.error('Error toggling configuration:', error);
      toast.error('Error al cambiar el estado de la configuración');
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingConfig(null);
    setFormData({
      name: '',
      description: '',
      feeType: 'percentage',
      feeValue: '',
      validFrom: new Date(),
      validTo: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
      appliesTo: 'ALL',
      selectedClientIds: [],
      minInvestment: '',
      maxInvestment: '',
      isActive: true
    });
    setValidationErrors({});
  };

  const formatDateRange = (validFrom, validTo) => {
    const from = new Date(validFrom).toLocaleDateString('es-ES');
    const to = new Date(validTo).toLocaleDateString('es-ES');
    return `${from} - ${to}`;
  };

  const getAppliesToBadge = (appliesTo) => {
    const variants = {
      'ALL': 'primary',
      'VIP_CLIENTS': 'warning',
      'PREMIUM_CLIENTS': 'info',
      'SMALL_CLIENTS': 'secondary',
      'SPECIFIC_CLIENTS': 'success'
    };
    return variants[appliesTo] || 'secondary';
  };

  if (loading) {
    return (
      <Container fluid className="py-4">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <p className="mt-2">Cargando configuraciones...</p>
        </div>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <h2>
            <FontAwesomeIcon icon="cog" className="me-2 text-primary" />
            Configuraciones de Fee Admin
          </h2>
          <p className="text-muted">Gestiona las configuraciones predefinidas de fees administrativos</p>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => setShowModal(true)}>
            <FontAwesomeIcon icon="plus" className="me-1" />
            Nueva Configuración
          </Button>
        </Col>
      </Row>

      {/* Stats Cards */}
      {stats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="bg-primary text-white">
              <Card.Body>
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="mb-0">{stats.total}</h4>
                    <p className="mb-0">Total</p>
                  </div>
                  <FontAwesomeIcon icon="list" size="2x" className="opacity-75" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="bg-success text-white">
              <Card.Body>
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="mb-0">{stats.active}</h4>
                    <p className="mb-0">Activas</p>
                  </div>
                  <FontAwesomeIcon icon="check-circle" size="2x" className="opacity-75" />
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="bg-warning text-white">
              <Card.Body>
                <div className="d-flex justify-content-between">
                  <div>
                    <h4 className="mb-0">{stats.inactive}</h4>
                    <p className="mb-0">Inactivas</p>
                  </div>
                  <FontAwesomeIcon icon="times-circle" size="2x" className="opacity-75" />
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Configurations Table */}
      <Card>
        <Card.Header>
          <h5 className="mb-0">
            <FontAwesomeIcon icon="table" className="me-2" />
            Configuraciones ({configurations.length})
          </h5>
        </Card.Header>
        <Card.Body className="p-0">
          {configurations.length === 0 ? (
            <div className="text-center py-5">
              <FontAwesomeIcon icon="info-circle" size="3x" className="text-muted mb-3" />
              <h5>No hay configuraciones</h5>
              <p className="text-muted">Crea la primera configuración de Fee Admin</p>
              <Button variant="primary" onClick={() => setShowModal(true)}>
                <FontAwesomeIcon icon="plus" className="me-1" />
                Crear Primera Configuración
              </Button>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Nombre</th>
                    <th>Tipo / Valor</th>
                    <th>Vigencia</th>
                    <th>Aplica a</th>
                    <th>Estado</th>
                    <th className="text-center">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {configurations.map((config) => (
                    <tr key={config.id}>
                      <td>
                        <div>
                          <strong>{config.name}</strong>
                          {config.description && (
                            <small className="d-block text-muted">{config.description}</small>
                          )}
                        </div>
                      </td>
                      <td>
                        <Badge 
                          bg={config.feeType === 'percentage' ? 'success' : 'warning'}
                          className="me-2"
                        >
                          <FontAwesomeIcon 
                            icon={config.feeType === 'percentage' ? 'percent' : 'dollar-sign'} 
                            className="me-1" 
                          />
                          {config.feeType === 'percentage' 
                            ? `${config.feeValue}%` 
                            : `$${config.feeValue.toLocaleString()}`
                          }
                        </Badge>
                      </td>
                      <td>
                        <small>{formatDateRange(config.validFrom, config.validTo)}</small>
                      </td>
                      <td>
                        <Badge bg={getAppliesToBadge(config.appliesTo)}>
                          {adminFeeService.formatAppliesTo(config.appliesTo)}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={config.isActive ? 'success' : 'secondary'}>
                          {config.isActive ? 'Activa' : 'Inactiva'}
                        </Badge>
                      </td>
                      <td className="text-center">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          className="me-1"
                          onClick={() => handleEdit(config)}
                          title="Editar"
                        >
                          <FontAwesomeIcon icon="edit" />
                        </Button>
                        <Button
                          variant={config.isActive ? 'outline-warning' : 'outline-success'}
                          size="sm"
                          className="me-1"
                          onClick={() => handleToggleActive(config)}
                          title={config.isActive ? 'Desactivar' : 'Activar'}
                        >
                          <FontAwesomeIcon icon={config.isActive ? 'pause' : 'play'} />
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => handleDelete(config)}
                          title="Eliminar"
                        >
                          <FontAwesomeIcon icon="trash" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Modal for Create/Edit Configuration */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={editingConfig ? 'edit' : 'plus'} className="me-2" />
            {editingConfig ? 'Editar Configuración' : 'Nueva Configuración'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Nombre *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    className={validationErrors.name ? 'is-invalid' : ''}
                    placeholder="Ej: Fee VIP 2025"
                  />
                  {validationErrors.name && (
                    <div className="invalid-feedback">{validationErrors.name}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Tipo y Valor *</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>
                      <ToggleButtonGroup 
                        type="radio" 
                        name="feeType" 
                        value={formData.feeType}
                        onChange={(value) => handleInputChange('feeType', value)}
                        size="sm"
                      >
                        <ToggleButton id="edit-percentage" value="percentage" variant="outline-success">
                          %
                        </ToggleButton>
                        <ToggleButton id="edit-amount" value="amount" variant="outline-warning">
                          $
                        </ToggleButton>
                      </ToggleButtonGroup>
                    </InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={formData.feeValue}
                      onChange={(e) => handleInputChange('feeValue', e.target.value)}
                      className={validationErrors.feeValue ? 'is-invalid' : ''}
                      placeholder="0.00"
                    />
                  </InputGroup>
                  {validationErrors.feeValue && (
                    <div className="invalid-feedback d-block">{validationErrors.feeValue}</div>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Group>
                  <Form.Label>Descripción</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    placeholder="Descripción opcional de la configuración"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Válido Desde *</Form.Label>
                  <div>
                    <DatePicker
                      selected={formData.validFrom}
                      onChange={(date) => handleInputChange('validFrom', date)}
                      dateFormat="dd/MM/yyyy"
                      className={`form-control ${validationErrors.validFrom ? 'is-invalid' : ''}`}
                    />
                  </div>
                  {validationErrors.validFrom && (
                    <div className="invalid-feedback d-block">{validationErrors.validFrom}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Válido Hasta *</Form.Label>
                  <div>
                    <DatePicker
                      selected={formData.validTo}
                      onChange={(date) => handleInputChange('validTo', date)}
                      dateFormat="dd/MM/yyyy"
                      className={`form-control ${validationErrors.validTo ? 'is-invalid' : ''}`}
                    />
                  </div>
                  {validationErrors.validTo && (
                    <div className="invalid-feedback d-block">{validationErrors.validTo}</div>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Aplica a *</Form.Label>
                  <Form.Select
                    value={formData.appliesTo}
                    onChange={(e) => handleInputChange('appliesTo', e.target.value)}
                  >
                    {adminFeeService.getAppliesToOptions().map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              {formData.appliesTo === 'SPECIFIC_CLIENTS' && (
                <Col xs={12}>
                  <Form.Group>
                    <Form.Label>Clientes Específicos *</Form.Label>
                    <Form.Select
                      multiple
                      value={formData.selectedClientIds}
                      onChange={(e) => {
                        const values = Array.from(e.target.selectedOptions, option => parseInt(option.value));
                        handleInputChange('selectedClientIds', values);
                      }}
                      className={validationErrors.selectedClientIds ? 'is-invalid' : ''}
                    >
                      {clients.map(client => (
                        <option key={client.ClientID} value={client.ClientID}>
                          {client.ClientName}
                        </option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted">
                      Mantén presionado Ctrl (Cmd en Mac) para seleccionar múltiples clientes
                    </Form.Text>
                    {validationErrors.selectedClientIds && (
                      <div className="invalid-feedback d-block">{validationErrors.selectedClientIds}</div>
                    )}
                  </Form.Group>
                </Col>
              )}
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Inversión Mínima</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={formData.minInvestment}
                      onChange={(e) => handleInputChange('minInvestment', e.target.value)}
                      placeholder="0.00"
                    />
                  </InputGroup>
                  <Form.Text className="text-muted">Opcional - Para uso futuro</Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Inversión Máxima</Form.Label>
                  <InputGroup>
                    <InputGroup.Text>$</InputGroup.Text>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={formData.maxInvestment}
                      onChange={(e) => handleInputChange('maxInvestment', e.target.value)}
                      className={validationErrors.maxInvestment ? 'is-invalid' : ''}
                      placeholder="0.00"
                    />
                  </InputGroup>
                  <Form.Text className="text-muted">Opcional - Para uso futuro</Form.Text>
                  {validationErrors.maxInvestment && (
                    <div className="invalid-feedback d-block">{validationErrors.maxInvestment}</div>
                  )}
                </Form.Group>
              </Col>
              <Col xs={12}>
                <Form.Check
                  type="checkbox"
                  label="Configuración activa"
                  checked={formData.isActive}
                  onChange={(e) => handleInputChange('isActive', e.target.checked)}
                />
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="check" className="me-1" />
                  {editingConfig ? 'Actualizar' : 'Crear'}
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon="exclamation-triangle" className="me-2 text-warning" />
            Confirmar Eliminación
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>¿Estás seguro de que deseas eliminar esta configuración?</p>
          {configToDelete && (
            <div className="bg-light p-3 rounded">
              <strong>Nombre:</strong> {configToDelete.name}<br/>
              <strong>Tipo:</strong> {adminFeeService.formatFeeType(configToDelete.feeType)}<br/>
              <strong>Valor:</strong> {configToDelete.feeType === 'percentage' 
                ? `${configToDelete.feeValue}%` 
                : `$${configToDelete.feeValue.toLocaleString()}`
              }
            </div>
          )}
          <Alert variant="danger" className="mt-3 mb-0">
            <FontAwesomeIcon icon="exclamation-circle" className="me-1" />
            Esta acción no se puede deshacer.
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            <FontAwesomeIcon icon="trash" className="me-1" />
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default AdminFeeConfigurationsPage; 